import React from 'react';

import { useThemeMode } from '@/nova/theme/OwsTheme';
// Loader + invoice live in the shared app-wide helper so all three checkouts
// (Spark module, configurator-v3, product-detail modal) reuse one Stripe instance
// and one invoice contract. Re-exported here to keep this module's API stable.
export { isStripeLive, getStripe, fetchInvoiceClientSecret, reconcileSessionInvoice } from '@/utils/stripe';
export type { InvoiceOutcome } from '@/utils/stripe';
import { CFG_STRIPE, CFG_STRIPE_LIGHT, CFG_FONT } from '../../../theme/configurator-tokens';

/**
 * Stripe wiring for the checkout sub-step — reproduces the v2 Spark integration
 * (`src/components/modules/spark/stripe-payment`):
 *   1. publishable key from `window.config.stripePublicKey`,
 *   2. the PaymentIntent clientSecret from `POST ${sessionApiUrl}/invoice { session_id }`
 *      (the backend computes the amount from the saved session; the secret arrives
 *      as `provider_reference`),
 *   3. payment success is confirmed server-side: the session reaches a
 *      post-payment status (`payment_successful` / `customization`), watched by
 *      the finish step via `session.status` (set by the WS / restored by sessionLoad).
 *
 * Falls back to a DEMO checkout (simulated success) only when no publishable key
 * is configured on the host page. The loader/invoice helpers now live in
 * `@/utils/stripe` (re-exported above); this module keeps only the
 * configurator-specific Elements appearance.
 */

/**
 * Stripe Elements appearance, themed to match the configurator chrome. (Used in
 * live mode; ignored in demo mode.) Ported 1:1 from the v2 Spark checkout
 * (`src/components/modules/spark/stripe-payment`) — the same `above` labels,
 * variables, and the full `.AccordionItem` / `.Label` / `.Input` / `.Error` /
 * `.TermsText` rule overrides.
 *
 * Built from a colour palette (`CFG_STRIPE` dark / `CFG_STRIPE_LIGHT` light) so it
 * follows the studio theme: Stripe renders Elements in a CROSS-ORIGIN iframe that
 * cannot read our `var(--ows-…)` custom properties, so the variant is chosen in
 * React via {@link useStripeAppearance} and passed as concrete literals. The base
 * `theme` key flips `night`⇄`stripe` so Stripe's own defaults start dark/light too.
 */
type StripePalette = { readonly [K in keyof typeof CFG_STRIPE]: string };

export function buildStripeAppearance(palette: StripePalette, mode: 'light' | 'dark') {
  return {
    theme: (mode === 'light' ? 'stripe' : 'night') as 'stripe' | 'night',
    labels: 'above' as const,
    variables: {
      colorBackground: palette.background,
      colorText: palette.text,
      colorPrimary: palette.primary,
      colorTextSecondary: palette.text,
      colorTextPlaceholder: palette.text,
      borderRadius: '12px',
      fontFamily: CFG_FONT,
      colorDanger: palette.danger,
      colorSuccess: palette.success,
    },
    rules: {
      '.AccordionItem': {
        boxShadow: 'none',
        borderColor: palette.accordionBorder,
        backgroundColor: palette.background,
      },
      '.AccordionItem--selected': { boxShadow: 'none' },
      '.AccordionItem:hover': { boxShadow: 'none' },
      '.AccordionItem:focus-visible': { boxShadow: 'none' },
      '.Label': {
        color: palette.label,
        fontSize: '14px',
        lineHeight: '110%',
        marginBottom: '6px',
      },
      '.Input': {
        boxShadow: 'none',
        border: `2px solid ${palette.inputBorder}`,
        backgroundColor: 'transparent',
        color: palette.inputText,
        fontSize: '16px',
        lineHeight: '24px',
        padding: '12px 14px',
      },
      '.Input--empty': { boxShadow: 'none' },
      '.Input--invalid': { boxShadow: 'none' },
      '.Input:hover': { boxShadow: 'none' },
      '.Input:focus': {
        boxShadow: 'none',
        border: `2px solid ${palette.inputBorderFocus}`,
      },
      '.Input:disabled': { boxShadow: 'none' },
      '.Input:autofill': { boxShadow: 'none' },
      '.Input::placeholder': {
        boxShadow: 'none',
        color: palette.inputPlaceholder,
        fontSize: '16px',
        lineHeight: '24px',
      },
      '.Input::selection': {
        boxShadow: 'none',
        color: palette.inputPlaceholder,
        fontSize: '16px',
        lineHeight: '24px',
      },
      '.Error': {
        fontSize: '14px',
        lineHeight: '110%',
        marginTop: '6px',
      },
      '.TermsText': {
        color: palette.label,
        fontSize: '14px',
        lineHeight: '110%',
      },
    },
  };
}

/** Dark appearance — the default for any non-React caller. */
export const STRIPE_APPEARANCE = buildStripeAppearance(CFG_STRIPE, 'dark');

/**
 * The Stripe appearance for the live checkout, resolved to the active studio
 * theme. Subscribing to `useThemeMode()` re-renders (and re-memoises) on a
 * light⇄dark switch so the embedded Stripe Elements re-theme live.
 */
export function useStripeAppearance(): ReturnType<typeof buildStripeAppearance> {
  const mode = useThemeMode();
  return React.useMemo(
    () => buildStripeAppearance(mode === 'light' ? CFG_STRIPE_LIGHT : CFG_STRIPE, mode),
    [mode],
  );
}
